import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useDecks } from '../../hooks/useDecks';
import { useMatches } from '../../hooks/useMatches';
import { useStatistics } from '../../hooks/useStatistics';
import StatisticsCard from '../Statistics/StatisticsCard';

const Dashboard: React.FC = () => {
  const { decks } = useDecks();
  const { matches } = useMatches();
  const { getOverallStatistics } = useStatistics(matches, decks);

  const overallStats = getOverallStatistics();
  const recentMatches = matches
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 10);

  const getDeckName = (deckId: string) => {
    return decks.find(deck => deck.id === deckId)?.name || 'Unknown Deck';
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        ダッシュボード
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <StatisticsCard
            title="全体統計"
            statistics={overallStats}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              登録済みデッキ
            </Typography>
            {decks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                デッキが登録されていません
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {decks.map((deck) => (
                  <Chip
                    key={deck.id}
                    label={deck.name}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          最近の対戦記録
        </Typography>
        {recentMatches.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            対戦記録がありません
          </Typography>
        ) : (
          <List>
            {recentMatches.map((match, index) => (
              <ListItem
                key={match.id}
                divider={index < recentMatches.length - 1}
                sx={{ px: 0 }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">
                        {getDeckName(match.userDeckId)} vs {match.opponentDeck}
                      </Typography>
                      <Chip
                        label={match.result === 'win' ? '勝利' : '敗北'}
                        color={match.result === 'win' ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={format(new Date(match.date), 'yyyy年M月d日', { locale: ja })}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default Dashboard;